export interface MarginPlanInputs {
  currentAge: number;
  retirementAge: number;
  retirementAccessAge: number;
  endAge: number;
  startingPortfolio: number;
  retirementAssets: number;
  externalAssets: number;
  externalAssetGrowthPercent: number;
  initialMargin: number;
  annualSpending: number;
  spendingDollarBasis: "today" | "retirement_start";
  spendingMode: "fixed" | "portfolio_percent";
  spendingPercent: number;
  spendingBorrowPercent: number;
  dividendYieldPercent: number;
  dividendTaxRatePercent: number;
  dividendUse: "spending" | "pay_margin";
  capitalGainsTaxEnabled: boolean;
  startingTaxableCostBasis: number;
  capitalGainsTaxRatePercent: number;
  flexibleSpendingEnabled: boolean;
  downMarketTriggerPercent: number;
  spendingCutWithdrawalRatePercent: number;
  downMarketSpendingReductionPercent: number;
  minimumSpendingPercent: number;
  monthlyContribution: number;
  contributionMode: "fixed" | "portfolio_percent";
  contributionPercent: number;
  contributionEndAge: number;
  contributionStrategy: "invest" | "pay_margin_first";
  cashFlowMode: "manual_contributions" | "income_expenses";
  annualWorkingIncome: number;
  incomeGrowthPercent: number;
  annualWorkingExpenses: number;
  workingExpenseInflationPercent: number;
  retirementIncomeMode: "none" | "coast";
  annualCoastIncome: number;
  coastIncomeGrowthPercent: number;
  coastIncomeEndAge: number;
  monthlySocialSecurityBenefit: number;
  socialSecurityStartAge: number;
  socialSecurityColaPercent: number;
  monthlyPensionBenefit: number;
  pensionStartAge: number;
  pensionColaPercent: number;
  expectedReturnPercent: number;
  volatilityPercent: number;
  marginRatePercent: number;
  marginRateMode: "fixed" | "range";
  marginRateMinPercent: number;
  marginRateMaxPercent: number;
  inflationPercent: number;
  maxLtvPercent: number;
  maintenanceShockEnabled: boolean;
  maintenanceShockTimingMode: "fixed" | "random_range";
  maintenanceShockAge: number;
  maintenanceShockLatestAge: number;
  maintenanceShockDurationYears: number;
  stressedMaxLtvPercent: number;
  pauseBorrowingLtvPercent: number;
  deleveragingLtvPercent: number;
  targetLtvPercent: number;
}

export interface MarginProjectionYear {
  year: number;
  age: number;
  portfolioStart: number;
  contribution: number;
  contributionInvested: number;
  marginRepaidFromContributions: number;
  portfolioGrowth: number;
  earnedIncome: number;
  socialSecurityIncome: number;
  pensionIncome: number;
  livingExpenses: number;
  marketDrawdownPercent: number;
  plannedWithdrawalRatePercent: number;
  spendingReduction: number;
  spendingTotal: number;
  spendingFunded: number;
  unfundedSpending: number;
  spendingBorrowed: number;
  spendingFromPortfolio: number;
  taxableAssetsSold: number;
  retirementAssetsSold: number;
  realizedCapitalGains: number;
  capitalGainsTaxes: number;
  dividendIncome: number;
  dividendTaxes: number;
  accessibleDividendIncome: number;
  inaccessibleRetirementDividendIncome: number;
  dividendUsedForSpending: number;
  marginRepaidFromDividends: number;
  dividendReinvested: number;
  marginInterest: number;
  marginRatePercent: number;
  deleveragingSale: number;
  portfolioEnd: number;
  taxablePortfolioEnd: number;
  taxableCostBasisEnd: number;
  retirementAssetsEnd: number;
  externalAssetsEnd: number;
  marginEnd: number;
  ltvPercent: number;
  netWorth: number;
  requiredEquity: number;
  maintenanceCushion: number;
  effectiveMaxLtvPercent: number;
  maintenanceBreach: boolean;
}

export interface SimulationResult {
  /** Combined collateral-and-spending success. */
  successPercent: number;
  successfulPaths: number;
  collateralSuccessPercent: number;
  collateralSuccessfulPaths: number;
  spendingSuccessPercent: number;
  spendingSuccessfulPaths: number;
  fullSpendingPercent: number;
  fullSpendingPaths: number;
  unmetSpendingPaths: number;
  simulations: number;
  deleveragingPaths: number;
  flexibleSpendingPaths: number;
  terminalNetWorth: { p10: number; p50: number; p90: number };
  failureBreakdown: {
    failedPaths: number;
    downMarketPaths: number;
    debtOutpacedPortfolioPaths: number;
    earliestBreachAge: number | null;
    medianBreachAge: number | null;
  };
  strategyComparison: {
    margin: StrategySimulationSummary;
    sellAssets: StrategySimulationSummary;
  };
}

export interface StrategySimulationSummary {
  combinedSuccessPercent: number;
  collateralSuccessPercent: number;
  spendingSuccessPercent: number;
  medianEndingNetWorth: number;
  medianPeakDebt: number;
  medianTotalInterest: number;
  medianCapitalGainsTaxes: number;
}

export interface PlanFactor {
  label: string;
  value: string;
  detail: string;
  tone: "positive" | "caution" | "critical";
}

export const DEFAULT_SIMULATION_COUNT = 2_000;

const PERCENT = 100;
const MONTHS = 12;

function finite(value: number, fallback = 0): number {
  return Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function planYears(inputs: MarginPlanInputs): number {
  return Math.min(100, Math.max(1, Math.round(finite(inputs.endAge) - finite(inputs.currentAge))));
}

function monthlyReturn(annualReturn: number): number {
  return Math.max(-0.999999, 1 + Math.max(-0.999999, annualReturn)) ** (1 / MONTHS) - 1;
}

function ltv(debt: number, portfolio: number): number {
  if (portfolio <= 0) return debt > 0 ? Number.POSITIVE_INFINITY : 0;
  return debt / portfolio * PERCENT;
}

function saleToTargetLtv(
  portfolio: number,
  debt: number,
  targetPercent: number,
  taxFraction: number,
): number {
  const target = clamp(targetPercent, 0, 99.9) / PERCENT;
  const afterTaxShare = 1 - clamp(taxFraction, 0, 0.999999);
  const denominator = afterTaxShare - target;
  const requiredSale = denominator > 0
    ? (debt - target * portfolio) / denominator
    : portfolio;
  return clamp(requiredSale, 0, Math.min(portfolio, debt / afterTaxShare));
}

function maintenanceShockActive(
  inputs: MarginPlanInputs,
  age: number,
  shockAge: number,
): boolean {
  if (!inputs.maintenanceShockEnabled || age < shockAge) return false;
  const duration = Math.max(0, finite(inputs.maintenanceShockDurationYears));
  return duration === 0 || age < shockAge + duration;
}

function sampleMaintenanceShockAge(inputs: MarginPlanInputs, random: () => number) {
  const configuredAge = finite(inputs.maintenanceShockAge);
  if (!inputs.maintenanceShockEnabled || inputs.maintenanceShockTimingMode !== "random_range") {
    return configuredAge;
  }
  const earliestAge = Math.ceil(Math.max(finite(inputs.currentAge), configuredAge));
  const latestAge = Math.floor(Math.min(
    finite(inputs.endAge),
    Math.max(earliestAge, finite(inputs.maintenanceShockLatestAge)),
  ));
  return earliestAge + Math.floor(random() * (latestAge - earliestAge + 1));
}

function stepPlan(
  inputs: MarginPlanInputs,
  monthlyReturns: number[],
  monthlyMarginRates?: number[],
  maintenanceShockAge = finite(inputs.maintenanceShockAge),
): MarginProjectionYear[] {
  const years = planYears(inputs);
  const rows: MarginProjectionYear[] = [];
  let portfolio = Math.max(0, finite(inputs.startingPortfolio));
  let taxableCostBasis = Math.max(0, finite(inputs.startingTaxableCostBasis));
  let retirementAssets = Math.max(0, finite(inputs.retirementAssets));
  let externalAssets = Math.max(0, finite(inputs.externalAssets));
  let margin = Math.max(0, finite(inputs.initialMargin));
  let marketIndex = 1;
  let marketHighWater = 1;
  let priorRetirementExpense = 0;
  const inflation = Math.max(-0.99, finite(inputs.inflationPercent)) / PERCENT;
  const externalMonthlyGrowth = monthlyReturn(
    Math.max(-0.99, finite(inputs.externalAssetGrowthPercent) / PERCENT),
  );
  const borrowShare = clamp(finite(inputs.spendingBorrowPercent), 0, PERCENT) / PERCENT;
  const monthlyDividendYield = Math.max(0, finite(inputs.dividendYieldPercent)) / PERCENT / MONTHS;
  const dividendTaxRate = clamp(finite(inputs.dividendTaxRatePercent), 0, PERCENT) / PERCENT;
  const capitalGainsTaxRate = inputs.capitalGainsTaxEnabled
    ? clamp(finite(inputs.capitalGainsTaxRatePercent), 0, PERCENT) / PERCENT
    : 0;
  const maxLtv = clamp(finite(inputs.maxLtvPercent), 0, PERCENT);
  const deleveragingLtv = clamp(finite(inputs.deleveragingLtvPercent), 0, maxLtv);
  const pauseLtv = clamp(finite(inputs.pauseBorrowingLtvPercent), 0, deleveragingLtv);
  const targetLtv = clamp(finite(inputs.targetLtvPercent), 0, deleveragingLtv);

  for (let year = 1; year <= years; year += 1) {
    const portfolioStart = portfolio + retirementAssets;
    const ageAtStart = finite(inputs.currentAge) + year - 1;
    const manualMonthlyContribution = inputs.contributionMode === "portfolio_percent"
      ? portfolioStart * Math.max(0, finite(inputs.contributionPercent)) / PERCENT / MONTHS
      : Math.max(0, finite(inputs.monthlyContribution));
    const retirementYearsElapsed = Math.max(
      0,
      Math.floor(ageAtStart - finite(inputs.retirementAge)),
    );
    const workingYearsElapsed = Math.max(0, Math.floor(ageAtStart - finite(inputs.currentAge)));
    const fixedSpendingInflationYears = inputs.spendingDollarBasis === "today"
      ? workingYearsElapsed
      : retirementYearsElapsed;
    const plannedRetirementExpense = ageAtStart >= finite(inputs.retirementAge)
      ? inputs.spendingMode === "portfolio_percent"
        ? portfolioStart * Math.max(0, finite(inputs.spendingPercent)) / PERCENT
        : Math.max(0, finite(inputs.annualSpending)) * (1 + inflation) ** fixedSpendingInflationYears
      : 0;
    const marketDrawdownPercent = marketHighWater > 0
      ? Math.max(0, (marketHighWater - marketIndex) / marketHighWater * PERCENT)
      : 0;
    const retirementYearsElapsedForIncome = Math.max(
      0,
      Math.floor(ageAtStart - finite(inputs.retirementAge)),
    );
    const workingIncome = inputs.cashFlowMode === "income_expenses"
      && ageAtStart < finite(inputs.retirementAge)
      ? Math.max(0, finite(inputs.annualWorkingIncome))
        * (1 + Math.max(-0.99, finite(inputs.incomeGrowthPercent)) / PERCENT) ** workingYearsElapsed
      : 0;
    const coastIncome = inputs.retirementIncomeMode === "coast"
      && ageAtStart >= finite(inputs.retirementAge)
      && ageAtStart < finite(inputs.coastIncomeEndAge)
      ? Math.max(0, finite(inputs.annualCoastIncome))
        * (1 + Math.max(-0.99, finite(inputs.coastIncomeGrowthPercent)) / PERCENT)
          ** retirementYearsElapsedForIncome
      : 0;
    const socialSecurityIncome = ageAtStart >= finite(inputs.socialSecurityStartAge)
      ? Math.max(0, finite(inputs.monthlySocialSecurityBenefit)) * MONTHS
        * (1 + Math.max(-0.99, finite(inputs.socialSecurityColaPercent)) / PERCENT)
          ** workingYearsElapsed
      : 0;
    const pensionYearsElapsed = Math.max(
      0,
      Math.floor(ageAtStart - finite(inputs.pensionStartAge)),
    );
    const pensionIncome = ageAtStart >= finite(inputs.pensionStartAge)
      ? Math.max(0, finite(inputs.monthlyPensionBenefit)) * MONTHS
        * (1 + Math.max(-0.99, finite(inputs.pensionColaPercent)) / PERCENT)
          ** pensionYearsElapsed
      : 0;
    const earnedIncome = workingIncome + coastIncome + socialSecurityIncome + pensionIncome;
    const plannedNetSpendingNeed = Math.max(0, plannedRetirementExpense - earnedIncome);
    const spendablePortfolioStart = portfolio
      + (ageAtStart >= finite(inputs.retirementAccessAge) ? retirementAssets : 0);
    const plannedWithdrawalRatePercent = spendablePortfolioStart > 0
      ? plannedNetSpendingNeed / spendablePortfolioStart * PERCENT
      : plannedNetSpendingNeed > 0 ? PERCENT : 0;
    const flexibleSpendingTriggered = inputs.flexibleSpendingEnabled
      && plannedRetirementExpense > 0
      && marketDrawdownPercent >= clamp(
        finite(inputs.downMarketTriggerPercent),
        0,
        PERCENT,
      )
      && plannedWithdrawalRatePercent >= clamp(
        finite(inputs.spendingCutWithdrawalRatePercent),
        0,
        PERCENT,
      );
    const spendingFloor = plannedRetirementExpense
      * clamp(finite(inputs.minimumSpendingPercent), 0, PERCENT) / PERCENT;
    const retirementExpense = flexibleSpendingTriggered
      ? Math.max(
          spendingFloor,
          (priorRetirementExpense || plannedRetirementExpense)
            * (1 - clamp(
              finite(inputs.downMarketSpendingReductionPercent),
              0,
              PERCENT,
            ) / PERCENT),
        )
      : plannedRetirementExpense;
    const spendingReduction = Math.max(0, plannedRetirementExpense - retirementExpense);
    const livingExpenses = ageAtStart < finite(inputs.retirementAge)
      && inputs.cashFlowMode === "income_expenses"
      ? Math.max(0, finite(inputs.annualWorkingExpenses))
        * (1 + Math.max(-0.99, finite(inputs.workingExpenseInflationPercent)) / PERCENT)
          ** workingYearsElapsed
      : retirementExpense;
    const cashSurplus = Math.max(0, earnedIncome - livingExpenses);
    const annualSpend = Math.max(0, livingExpenses - earnedIncome);
    const monthlyContributionAmount = inputs.cashFlowMode === "income_expenses"
      ? cashSurplus / MONTHS
      : manualMonthlyContribution;
    let contribution = 0;
    let contributionInvested = 0;
    let marginRepaidFromContributions = 0;
    let portfolioGrowth = 0;
    let spendingBorrowed = 0;
    let spendingFromPortfolio = 0;
    let taxableAssetsSold = 0;
    let retirementAssetsSold = 0;
    let realizedCapitalGains = 0;
    let capitalGainsTaxes = 0;
    let spendingFunded = 0;
    let unfundedSpending = 0;
    let dividendIncome = 0;
    let dividendTaxes = 0;
    let accessibleDividendIncome = 0;
    let inaccessibleRetirementDividendIncome = 0;
    let dividendUsedForSpending = 0;
    let marginRepaidFromDividends = 0;
    let dividendReinvested = 0;
    let marginInterest = 0;
    let deleveragingSale = 0;
    let maintenanceBreach = false;
    let rateTotal = 0;

    for (let month = 0; month < MONTHS; month += 1) {
      const index = (year - 1) * MONTHS + month;
      const age = ageAtStart + month / MONTHS;
      const monthContribution = inputs.cashFlowMode === "income_expenses"
        ? monthlyContributionAmount
        : age < finite(inputs.contributionEndAge)
          ? monthlyContributionAmount
          : 0;
      const marginRepayment = inputs.contributionStrategy === "pay_margin_first"
        ? Math.min(margin, monthContribution)
        : 0;
      const investedContribution = monthContribution - marginRepayment;
      margin -= marginRepayment;
      portfolio += investedContribution;
      taxableCostBasis += investedContribution;
      contribution += monthContribution;
      contributionInvested += investedContribution;
      marginRepaidFromContributions += marginRepayment;

      const monthlyPortfolioReturn = Math.max(-0.999999, finite(monthlyReturns[index]));
      const growth = portfolio * monthlyPortfolioReturn;
      const retirementGrowth = retirementAssets * monthlyPortfolioReturn;
      portfolio = Math.max(0, portfolio + growth);
      retirementAssets = Math.max(0, retirementAssets + retirementGrowth);
      portfolioGrowth += growth + retirementGrowth;
      externalAssets *= 1 + externalMonthlyGrowth;
      marketIndex *= 1 + Math.max(-0.999999, finite(monthlyReturns[index]));
      marketHighWater = Math.max(marketHighWater, marketIndex);

      const monthSpend = annualSpend / MONTHS;
      const taxableDividend = Math.min(
        portfolio,
        Math.max(0, portfolio - growth) * monthlyDividendYield,
      );
      const retirementDividend = Math.min(
        retirementAssets,
        Math.max(0, retirementAssets - retirementGrowth) * monthlyDividendYield,
      );
      const taxableDividendTax = taxableDividend * dividendTaxRate;
      const netTaxableDividend = taxableDividend - taxableDividendTax;
      portfolio -= taxableDividend;
      retirementAssets -= retirementDividend;
      dividendIncome += taxableDividend + retirementDividend;
      dividendTaxes += taxableDividendTax;
      const retirementAvailable = age >= finite(inputs.retirementAccessAge);
      accessibleDividendIncome += netTaxableDividend
        + (retirementAvailable ? retirementDividend : 0);
      if (!retirementAvailable) inaccessibleRetirementDividendIncome += retirementDividend;
      let taxableDividendRemaining = netTaxableDividend;
      let retirementDividendRemaining = retirementAvailable ? retirementDividend : 0;
      if (!retirementAvailable) retirementAssets += retirementDividend;
      const repayMarginFromDividends = () => {
        const accessibleDividend = taxableDividendRemaining + retirementDividendRemaining;
        const dividendRepayment = Math.min(margin, accessibleDividend);
        const taxableDividendRepaid = Math.min(taxableDividendRemaining, dividendRepayment);
        taxableDividendRemaining -= taxableDividendRepaid;
        retirementDividendRemaining -= dividendRepayment - taxableDividendRepaid;
        margin -= dividendRepayment;
        marginRepaidFromDividends += dividendRepayment;
      };
      if (inputs.dividendUse === "pay_margin") repayMarginFromDividends();
      // Once any existing margin balance has been paid, remaining accessible dividends
      // fund spending before the plan borrows or sells assets. This avoids borrowing and
      // immediately repaying the same dollars when dividend income exceeds the debt.
      const accessibleDividend = taxableDividendRemaining + retirementDividendRemaining;
      const spendingDividend = Math.min(accessibleDividend, monthSpend);
      const taxableDividendSpent = Math.min(taxableDividendRemaining, spendingDividend);
      taxableDividendRemaining -= taxableDividendSpent;
      retirementDividendRemaining -= spendingDividend - taxableDividendSpent;
      dividendUsedForSpending += spendingDividend;
      if (inputs.dividendUse === "spending") repayMarginFromDividends();
      const spendAfterDividends = monthSpend - spendingDividend;
      const effectiveMaxLtv = maintenanceShockActive(inputs, age, maintenanceShockAge)
        ? Math.min(maxLtv, clamp(finite(inputs.stressedMaxLtvPercent), 0, PERCENT))
        : maxLtv;
      const effectivePauseLtv = Math.min(pauseLtv, effectiveMaxLtv);
      const effectiveDeleveragingLtv = Math.min(deleveragingLtv, effectiveMaxLtv);
      const effectiveTargetLtv = Math.min(targetLtv, effectiveDeleveragingLtv);
      const borrowingPaused = ltv(margin, portfolio) >= effectivePauseLtv;
      const intendedBorrow = borrowingPaused ? 0 : spendAfterDividends * borrowShare;
      const intendedSale = spendAfterDividends - intendedBorrow;
      const currentGainFraction = portfolio > 0
        ? Math.max(0, 1 - taxableCostBasis / portfolio)
        : 0;
      const saleTaxFraction = currentGainFraction * capitalGainsTaxRate;
      const grossTaxableSale = Math.min(
        portfolio,
        intendedSale / Math.max(0.000001, 1 - saleTaxFraction),
      );
      const basisAllocatedToSpendingSale = portfolio > 0
        ? Math.min(taxableCostBasis, grossTaxableSale * taxableCostBasis / portfolio)
        : 0;
      const spendingSaleGain = Math.max(0, grossTaxableSale - basisAllocatedToSpendingSale);
      const spendingSaleTax = spendingSaleGain * capitalGainsTaxRate;
      const taxableSaleProceeds = grossTaxableSale - spendingSaleTax;
      portfolio -= grossTaxableSale;
      taxableCostBasis -= basisAllocatedToSpendingSale;
      taxableAssetsSold += grossTaxableSale;
      realizedCapitalGains += spendingSaleGain;
      capitalGainsTaxes += spendingSaleTax;
      const remainingSale = Math.max(0, intendedSale - taxableSaleProceeds);
      const retirementSale = retirementAvailable
        ? Math.min(retirementAssets, remainingSale)
        : 0;
      retirementAssets -= retirementSale;
      retirementAssetsSold += retirementSale;
      const actualSale = taxableSaleProceeds + retirementSale;
      spendingFromPortfolio += actualSale;
      const availableCredit = Math.max(0, portfolio * effectiveMaxLtv / PERCENT - margin);
      const borrowed = Math.min(intendedBorrow, availableCredit);
      margin += borrowed;
      spendingBorrowed += borrowed;
      const funded = spendingDividend + actualSale + borrowed;
      spendingFunded += funded;
      unfundedSpending += Math.max(0, monthSpend - funded);

      dividendReinvested += taxableDividendRemaining + retirementDividendRemaining;
      portfolio += taxableDividendRemaining;
      taxableCostBasis += taxableDividendRemaining;
      retirementAssets += retirementDividendRemaining;

      const annualRate = Math.max(0, finite(monthlyMarginRates?.[index] ?? inputs.marginRatePercent));
      rateTotal += annualRate;
      const interest = margin * annualRate / PERCENT / MONTHS;
      margin += interest;
      marginInterest += interest;

      const beforePolicyLtv = ltv(margin, portfolio);
      if (!Number.isFinite(beforePolicyLtv) || beforePolicyLtv > effectiveMaxLtv) maintenanceBreach = true;
      if (beforePolicyLtv > effectiveDeleveragingLtv && margin > 0 && portfolio > 0) {
        const gainFraction = Math.max(0, 1 - taxableCostBasis / portfolio);
        const taxFraction = gainFraction * capitalGainsTaxRate;
        const sale = saleToTargetLtv(portfolio, margin, effectiveTargetLtv, taxFraction);
        const basisAllocated = Math.min(taxableCostBasis, sale * taxableCostBasis / portfolio);
        const realizedGain = Math.max(0, sale - basisAllocated);
        const tax = realizedGain * capitalGainsTaxRate;
        const debtRepayment = Math.min(margin, sale - tax);
        portfolio -= sale;
        taxableCostBasis -= basisAllocated;
        margin -= debtRepayment;
        taxableAssetsSold += sale;
        realizedCapitalGains += realizedGain;
        capitalGainsTaxes += tax;
        deleveragingSale += sale;
      }
    }

    const ltvPercent = ltv(margin, portfolio);
    const effectiveMaxLtvPercent = maintenanceShockActive(
      inputs,
      ageAtStart,
      maintenanceShockAge,
    )
      ? Math.min(maxLtv, clamp(finite(inputs.stressedMaxLtvPercent), 0, PERCENT))
      : maxLtv;
    const requiredEquity = portfolio * (PERCENT - effectiveMaxLtvPercent) / PERCENT;
    const totalPortfolio = portfolio + retirementAssets;
    const netWorth = totalPortfolio + externalAssets - margin;
    rows.push({
      year,
      age: finite(inputs.currentAge) + year,
      portfolioStart,
      contribution,
      contributionInvested,
      marginRepaidFromContributions,
      portfolioGrowth,
      earnedIncome,
      socialSecurityIncome,
      pensionIncome,
      livingExpenses,
      marketDrawdownPercent,
      plannedWithdrawalRatePercent,
      spendingReduction,
      spendingTotal: annualSpend,
      spendingFunded,
      unfundedSpending,
      spendingBorrowed,
      spendingFromPortfolio,
      taxableAssetsSold,
      retirementAssetsSold,
      realizedCapitalGains,
      capitalGainsTaxes,
      dividendIncome,
      dividendTaxes,
      accessibleDividendIncome,
      inaccessibleRetirementDividendIncome,
      dividendUsedForSpending,
      marginRepaidFromDividends,
      dividendReinvested,
      marginInterest,
      marginRatePercent: rateTotal / MONTHS,
      deleveragingSale,
      portfolioEnd: totalPortfolio,
      taxablePortfolioEnd: portfolio,
      taxableCostBasisEnd: taxableCostBasis,
      retirementAssetsEnd: retirementAssets,
      externalAssetsEnd: externalAssets,
      marginEnd: margin,
      ltvPercent,
      netWorth,
      requiredEquity,
      maintenanceCushion: portfolio - margin - requiredEquity,
      effectiveMaxLtvPercent,
      maintenanceBreach,
    });
    if (plannedRetirementExpense > 0) priorRetirementExpense = retirementExpense;
  }
  return rows;
}

export function projectMarginPlan(inputs: MarginPlanInputs): MarginProjectionYear[] {
  const months = planYears(inputs) * MONTHS;
  const rate = monthlyReturn(finite(inputs.expectedReturnPercent) / PERCENT);
  return stepPlan(inputs, Array.from({ length: months }, () => rate));
}

function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

function normal(random: () => number): number {
  const u = Math.max(Number.EPSILON, random());
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * random());
}

function percentile(sorted: number[], probability: number): number {
  if (sorted.length === 0) return 0;
  const index = (sorted.length - 1) * probability;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  return lower === upper
    ? sorted[lower]
    : sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
}

export function simulateMarginPlan(
  inputs: MarginPlanInputs,
  simulations = DEFAULT_SIMULATION_COUNT,
  seed = 20_261_017,
): SimulationResult {
  const count = Math.max(1, Math.round(simulations));
  const months = planYears(inputs) * MONTHS;
  const random = seededRandom(seed);
  const compoundReturn = finite(inputs.expectedReturnPercent) / PERCENT;
  const volatility = Math.max(0, finite(inputs.volatilityPercent)) / PERCENT;
  // Expected return is a compound annual growth assumption, matching the deterministic
  // projection and the linked-portfolio CAGR estimate. Calibrate the lognormal variance
  // so volatility remains the annual standard deviation of simple returns without
  // subtracting volatility drag from the user's CAGR a second time.
  const medianAnnualGrowth = Math.max(0.01, 1 + compoundReturn);
  const relativeVariance = (volatility / medianAnnualGrowth) ** 2;
  const annualGrowthVarianceFactor = (1 + Math.sqrt(1 + 4 * relativeVariance)) / 2;
  const annualLogVariance = Math.log(annualGrowthVarianceFactor);
  const monthlyLogSigma = Math.sqrt(annualLogVariance / MONTHS);
  const monthlyLogMean = Math.log(medianAnnualGrowth) / MONTHS;
  let successfulPaths = 0;
  let collateralSuccessfulPaths = 0;
  let spendingSuccessfulPaths = 0;
  let fullSpendingPaths = 0;
  let unmetSpendingPaths = 0;
  let deleveragingPaths = 0;
  let flexibleSpendingPaths = 0;
  const terminalNetWorth: number[] = [];
  let downMarketPaths = 0;
  let debtOutpacedPortfolioPaths = 0;
  const firstBreachAges: number[] = [];
  const comparison = {
    margin: {
      combined: 0,
      collateral: 0,
      spending: 0,
      endingNetWorth: [] as number[],
      peakDebt: [] as number[],
      totalInterest: [] as number[],
      capitalGainsTaxes: [] as number[],
    },
    sellAssets: {
      combined: 0,
      collateral: 0,
      spending: 0,
      endingNetWorth: [] as number[],
      peakDebt: [] as number[],
      totalInterest: [] as number[],
      capitalGainsTaxes: [] as number[],
    },
  };

  const recordComparison = (
    target: typeof comparison.margin,
    path: MarginProjectionYear[],
  ) => {
    const collateralSurvived = !path.some((row) => row.maintenanceBreach);
    const spendingMet = !path.some((row) => row.unfundedSpending > 0.005);
    if (collateralSurvived) target.collateral += 1;
    if (spendingMet) target.spending += 1;
    if (collateralSurvived && spendingMet) target.combined += 1;
    target.endingNetWorth.push(path.at(-1)?.netWorth ?? 0);
    target.peakDebt.push(Math.max(0, ...path.map((row) => row.marginEnd)));
    target.totalInterest.push(path.reduce((sum, row) => sum + row.marginInterest, 0));
    target.capitalGainsTaxes.push(path.reduce((sum, row) => sum + row.capitalGainsTaxes, 0));
  };

  for (let simulation = 0; simulation < count; simulation += 1) {
    const returns = Array.from(
      { length: months },
      () => Math.exp(monthlyLogMean + monthlyLogSigma * normal(random)) - 1,
    );
    const minRate = Math.max(0, Math.min(finite(inputs.marginRateMinPercent), finite(inputs.marginRateMaxPercent)));
    const maxRate = Math.max(minRate, Math.max(finite(inputs.marginRateMinPercent), finite(inputs.marginRateMaxPercent)));
    const rates = inputs.marginRateMode === "range"
      ? Array.from({ length: Math.ceil(months / MONTHS) }, () => minRate + random() * (maxRate - minRate))
        .flatMap((rate) => Array.from({ length: MONTHS }, () => rate))
      : undefined;
    const maintenanceShockAge = sampleMaintenanceShockAge(inputs, random);
    const path = stepPlan(inputs, returns, rates, maintenanceShockAge);
    const marginPath = stepPlan(
      { ...inputs, spendingBorrowPercent: PERCENT },
      returns,
      rates,
      maintenanceShockAge,
    );
    const sellPath = stepPlan({
      ...inputs,
      spendingBorrowPercent: 0,
      dividendUse: "spending",
    }, returns, rates, maintenanceShockAge);
    recordComparison(comparison.margin, marginPath);
    recordComparison(comparison.sellAssets, sellPath);
    if (path.some((row) => row.deleveragingSale > 0)) deleveragingPaths += 1;
    if (path.some((row) => row.spendingReduction > 0)) flexibleSpendingPaths += 1;
    const firstBreach = path.find((row) => row.maintenanceBreach);
    const spendingMet = !path.some((row) => row.unfundedSpending > 0.005);
    const fullSpendingMet = spendingMet && !path.some((row) => row.spendingReduction > 0.005);
    if (!firstBreach) collateralSuccessfulPaths += 1;
    if (spendingMet) spendingSuccessfulPaths += 1;
    else unmetSpendingPaths += 1;
    if (fullSpendingMet) fullSpendingPaths += 1;
    if (firstBreach) {
      firstBreachAges.push(firstBreach.age);
      if (firstBreach.portfolioGrowth < 0) downMarketPaths += 1;
      else debtOutpacedPortfolioPaths += 1;
    } else if (spendingMet) successfulPaths += 1;
    terminalNetWorth.push(path.at(-1)?.netWorth ?? 0);
  }

  terminalNetWorth.sort((a, b) => a - b);
  firstBreachAges.sort((a, b) => a - b);
  const comparisonSummary = (entry: typeof comparison.margin): StrategySimulationSummary => {
    entry.endingNetWorth.sort((a, b) => a - b);
    entry.peakDebt.sort((a, b) => a - b);
    entry.totalInterest.sort((a, b) => a - b);
    entry.capitalGainsTaxes.sort((a, b) => a - b);
    return {
      combinedSuccessPercent: entry.combined / count * PERCENT,
      collateralSuccessPercent: entry.collateral / count * PERCENT,
      spendingSuccessPercent: entry.spending / count * PERCENT,
      medianEndingNetWorth: percentile(entry.endingNetWorth, 0.5),
      medianPeakDebt: percentile(entry.peakDebt, 0.5),
      medianTotalInterest: percentile(entry.totalInterest, 0.5),
      medianCapitalGainsTaxes: percentile(entry.capitalGainsTaxes, 0.5),
    };
  };
  return {
    successPercent: successfulPaths / count * PERCENT,
    successfulPaths,
    collateralSuccessPercent: collateralSuccessfulPaths / count * PERCENT,
    collateralSuccessfulPaths,
    spendingSuccessPercent: spendingSuccessfulPaths / count * PERCENT,
    spendingSuccessfulPaths,
    fullSpendingPercent: fullSpendingPaths / count * PERCENT,
    fullSpendingPaths,
    unmetSpendingPaths,
    simulations: count,
    deleveragingPaths,
    flexibleSpendingPaths,
    terminalNetWorth: {
      p10: percentile(terminalNetWorth, 0.1),
      p50: percentile(terminalNetWorth, 0.5),
      p90: percentile(terminalNetWorth, 0.9),
    },
    failureBreakdown: {
      failedPaths: count - collateralSuccessfulPaths,
      downMarketPaths,
      debtOutpacedPortfolioPaths,
      earliestBreachAge: firstBreachAges[0] ?? null,
      medianBreachAge: firstBreachAges.length > 0 ? percentile(firstBreachAges, 0.5) : null,
    },
    strategyComparison: {
      margin: comparisonSummary(comparison.margin),
      sellAssets: comparisonSummary(comparison.sellAssets),
    },
  };
}

export function planFactors(inputs: MarginPlanInputs, projection: MarginProjectionYear[]): PlanFactor[] {
  const startingPortfolio = Math.max(
    1,
    finite(inputs.startingPortfolio) + finite(inputs.retirementAssets),
  );
  const firstRetirementYear = projection.find(
    (row) => row.age - 1 >= finite(inputs.retirementAge),
  );
  const firstRetirementIndex = firstRetirementYear
    ? projection.indexOf(firstRetirementYear)
    : -1;
  const retirementStartDebt = firstRetirementIndex <= 0
    ? Math.max(0, finite(inputs.initialMargin))
    : projection[firstRetirementIndex - 1].marginEnd;
  const retirementStartNetInvestments = Math.max(
    1,
    (firstRetirementYear?.portfolioStart ?? startingPortfolio) - retirementStartDebt,
  );
  const withdrawalRate = Math.max(0, firstRetirementYear?.spendingTotal ?? 0)
    / retirementStartNetInvestments * PERCENT;
  const initialLtv = Math.max(0, finite(inputs.initialMargin))
    / Math.max(1, finite(inputs.startingPortfolio)) * PERCENT;
  const peakLtv = Math.max(initialLtv, ...projection.map((row) => row.ltvPercent));
  const maxLtv = Math.max(0, finite(inputs.maxLtvPercent));
  const maintenanceRequirement = Math.max(0, PERCENT - maxLtv);
  const requiredEquity = Math.max(0, finite(inputs.startingPortfolio)) * maintenanceRequirement / PERCENT;
  const minimumCushion = Math.min(...projection.map((row) => row.maintenanceCushion));

  return [
    {
      label: "Starting draw",
      value: `${withdrawalRate.toFixed(2)}%`,
      detail: inputs.spendingMode === "portfolio_percent"
        ? "First retirement-year net spending need as a share of projected investments after margin debt; the gross expense resets each year with portfolio value."
        : "First retirement-year net spending need as a share of projected investments after margin debt at retirement.",
      tone: withdrawalRate <= 3 ? "positive" : withdrawalRate <= 4 ? "caution" : "critical",
    },
    {
      label: "Spending funded by margin",
      value: `${clamp(finite(inputs.spendingBorrowPercent), 0, PERCENT).toFixed(0)}%`,
      detail: `The remainder is sold from the portfolio. New borrowing pauses at ${finite(inputs.pauseBorrowingLtvPercent).toFixed(0)}% LTV.`,
      tone: inputs.spendingBorrowPercent <= 50 ? "positive" : inputs.spendingBorrowPercent < 100 ? "caution" : "critical",
    },
    ...(inputs.dividendYieldPercent > 0 ? [{
      label: "Dividend cash",
      value: `${clamp(finite(inputs.dividendYieldPercent), 0, PERCENT).toFixed(2)}% yield`,
      detail: inputs.dividendUse === "pay_margin"
        ? `Carved out of total return, reduced by the ${clamp(finite(inputs.dividendTaxRatePercent), 0, PERCENT).toFixed(1)}% taxable-account tax assumption, applied to existing margin debt first, then to spending; any excess is reinvested.`
        : `Carved out of total return, reduced by the ${clamp(finite(inputs.dividendTaxRatePercent), 0, PERCENT).toFixed(1)}% taxable-account tax assumption, applied to spending first, then to existing margin debt; any excess is reinvested.`,
      tone: "positive" as const,
    }] : []),
    ...(inputs.capitalGainsTaxEnabled ? [{
      label: "Capital-gains estimate",
      value: `${clamp(finite(inputs.capitalGainsTaxRatePercent), 0, PERCENT).toFixed(1)}%`,
      detail: `Applied only to the estimated gain portion of taxable sales using ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Math.max(0, finite(inputs.startingTaxableCostBasis)))} of starting aggregate basis.`,
      tone: "caution" as const,
    }] : []),
    ...(inputs.flexibleSpendingEnabled ? [{
      label: "Down-market spending",
      value: `${clamp(finite(inputs.downMarketSpendingReductionPercent), 0, PERCENT).toFixed(0)}% cut`,
      detail: `Applied only when the market drawdown reaches ${clamp(finite(inputs.downMarketTriggerPercent), 0, PERCENT).toFixed(0)}% and the pre-cut net withdrawal rate reaches ${clamp(finite(inputs.spendingCutWithdrawalRatePercent), 0, PERCENT).toFixed(1)}%, with a ${clamp(finite(inputs.minimumSpendingPercent), 0, PERCENT).toFixed(0)}% spending floor.`,
      tone: "positive" as const,
    }] : []),
    {
      label: "Initial LTV",
      value: `${initialLtv.toFixed(2)}%`,
      detail: "Margin debt relative to the portfolio at the start.",
      tone: initialLtv <= maxLtv * 0.25 ? "positive" : initialLtv < maxLtv * 0.6 ? "caution" : "critical",
    },
    {
      label: "Required equity now",
      value: new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(requiredEquity),
      detail: `${maintenanceRequirement.toFixed(1)}% portfolio maintenance assumption; verify the weighted requirement with your broker.`,
      tone: initialLtv <= maxLtv * 0.6 ? "positive" : initialLtv <= maxLtv ? "caution" : "critical",
    },
    {
      label: "Minimum baseline cushion",
      value: new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(minimumCushion),
      detail: `Equity above maintenance after monthly accrual and guardrails; peak ending LTV is ${Number.isFinite(peakLtv) ? `${peakLtv.toFixed(1)}%` : "over limit"}.`,
      tone: minimumCushion > startingPortfolio * 0.1 ? "positive" : minimumCushion >= 0 ? "caution" : "critical",
    },
  ];
}
